/**
 * Feedback, one status per item: open, resolved, noise, praise. Admins close
 * open items with a note, reopen closed ones, or re-categorize (the category
 * is the route: knowledge_base queues the KB webhook handoff).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { agentBaseUrl } from '../../lib/agentApi.js';
import type {
  FeedbackCounts,
  FeedbackItem,
  FeedbackListResponse,
  FeedbackStatus,
  ReviewTranscriptResponse,
} from '../../lib/types.js';

export type FeedbackStatusFilter = FeedbackStatus | 'all';
export type FeedbackCategoryFilter = FeedbackItem['triageCategory'] | 'all';

const LIST_KEY = ['feedback'];

export const useFeedbackListLogic = (
  status: FeedbackStatusFilter,
  category: FeedbackCategoryFilter,
) =>
  useQuery({
    queryKey: [...LIST_KEY, status, category],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (status !== 'all') params.set('status', status);
      if (category !== null && category !== 'all') params.set('triageCategory', category);
      return apiRequest<FeedbackListResponse>(`/api/feedback?${params.toString()}`);
    },
    refetchInterval: 30_000,
  });

/** Tab counts, under the same category filter as the list. */
export const useFeedbackCountsLogic = (category: FeedbackCategoryFilter) =>
  useQuery({
    queryKey: [...LIST_KEY, 'counts', category],
    queryFn: () => {
      const params = new URLSearchParams();
      if (category !== null && category !== 'all') params.set('triageCategory', category);
      return apiRequest<FeedbackCounts>(`/api/feedback/counts?${params.toString()}`);
    },
    refetchInterval: 30_000,
  });

/**
 * The rated exchange (or, with isolate=false, the whole conversation) from
 * the orchestration review route. Fetched lazily on expand; conversations are
 * append-only so the isolated turn never changes (cache forever); the full
 * thread can grow, so it refetches on each expand.
 */
export const useReviewTranscript = (
  conversationId: string,
  messageId: string,
  isolate: boolean,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ['feedback-transcript', conversationId, isolate ? messageId : 'full'],
    queryFn: () =>
      apiRequest<ReviewTranscriptResponse>(
        `/api/agent/review/threads/${encodeURIComponent(conversationId)}/messages${
          isolate ? `?messageId=${encodeURIComponent(messageId)}` : ''
        }`,
        { baseUrl: agentBaseUrl },
      ),
    enabled,
    staleTime: isolate ? Infinity : 0,
  });

export type CloseAs = 'resolved' | 'noise';

type StatusInput =
  | { feedbackId: string; status: CloseAs; note: string }
  | { feedbackId: string; status: 'open' };

/** Close an item as resolved or noise (note required), or reopen it. */
export const useSetFeedbackStatusLogic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StatusInput) =>
      apiRequest<FeedbackItem>(`/api/feedback/${encodeURIComponent(input.feedbackId)}`, {
        method: 'PATCH',
        body:
          input.status === 'open'
            ? { status: 'open' }
            : { status: input.status, resolutionNote: input.note },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
};

interface RecategorizeInput {
  feedbackId: string;
  category: NonNullable<FeedbackItem['triageCategory']>;
}

/** The category IS the route: knowledge_base queues the KB webhook handoff
 * server-side; other categories re-label and wait for their lanes. */
export const useRecategorizeLogic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ feedbackId, category }: RecategorizeInput) =>
      apiRequest<{ accepted: true }>(`/api/feedback/${encodeURIComponent(feedbackId)}/category`, {
        method: 'PATCH',
        body: { category },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
};
